// frontend/src/components/Individual/CreateRoom.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import socket from '../../services/socket';
import VoiceRecorder from '../Shared/VoiceRecorder';
import Toast, { useToast } from '../Shared/Toast';
import AiContextModal from '../Shared/AiContextModal';
import { useScreenShare } from '../../hooks/useScreenShare';

const CreateRoom = ({ user, onLogout }) => {
  const [roomKey, setRoomKey] = useState('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [finalTranscripts, setFinalTranscripts] = useState([]);
  const [liveText, setLiveText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [sessionKey, setSessionKey] = useState('');
  const [showSessionInput, setShowSessionInput] = useState(true);
  const [sessionError, setSessionError] = useState('');

  // AI interview assistant state
  const [aiEnabled, setAiEnabled] = useState(false);
  const [stealthMode, setStealthMode] = useState(true);
  const [aiMode, setAiMode] = useState('coach'); // 'coach' | 'answer'
  const [aiResponses, setAiResponses] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showAiContextModal, setShowAiContextModal] = useState(false);
  const [aiRoleLabel, setAiRoleLabel] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [aiSystemPrompt, setAiSystemPrompt] = useState('');

  const [features, setFeatures] = useState({ screenShare: false, remoteControl: false });
  const ss = useScreenShare({ socket });

  const { toast, showToast } = useToast();
  const transcriptEndRef = useRef(null);
  const aiEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/settings/features').then(r => setFeatures(r.data)).catch(() => {});
  }, []);

  // Auto-scroll
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [finalTranscripts, liveText]);

  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiResponses, aiThinking]);

  // Emit AI mode changes to backend
  const emitAiMode = (updates) => {
    const next = {
      aiEnabled: updates.aiEnabled ?? aiEnabled,
      stealthMode: updates.stealthMode ?? stealthMode,
      mode: updates.aiMode ?? aiMode
    };
    socket.emit('set-ai-mode', next);
  };

  const handleAiToggle = () => {
    if (!aiEnabled) {
      // Turning ON → show context modal first
      setShowAiContextModal(true);
    } else {
      // Turning OFF → disable immediately
      setAiEnabled(false);
      setAiRoleLabel('');
      setAiResponses([]);
      emitAiMode({ aiEnabled: false });
    }
  };

  const handleAiContextConfirm = (systemPrompt, roleLabel) => {
    setShowAiContextModal(false);
    setAiSystemPrompt(systemPrompt);
    setAiRoleLabel(roleLabel);
    setAiEnabled(true);
    // Send mode + custom system prompt to backend
    const next = {
      aiEnabled: true,
      stealthMode,
      mode: aiMode
    };
    socket.emit('set-ai-mode', next);
    if (systemPrompt) {
      socket.emit('switch-agent', { agent: roleLabel, systemPrompt });
    }
  };

  const handleModeChange = (mode) => {
    setAiMode(mode);
    setAiResponses([]);
    emitAiMode({ aiMode: mode });
  };

  const handleStealthToggle = () => {
    const next = !stealthMode;
    setStealthMode(next);
    emitAiMode({ stealthMode: next });
  };

  useEffect(() => {
    if (!roomKey) return;

    socket.on('transcript', (data) => {
      if (data.isFinal) {
        setFinalTranscripts((prev) => [...prev, { text: data.text, timestamp: data.timestamp }]);
        setLiveText('');
      } else {
        setLiveText(data.text);
      }
    });

    socket.on('message', (data) => {
      setMessages((prev) => [...prev, {
        message: data.message,
        userId: data.userId,
        userName: data.userName || `User ${String(data.userId).slice(-4)}`,
        timestamp: data.timestamp
      }]);
    });

    socket.on('ai-thinking', (data) => {
      setAiThinking(data.thinking);
    });

    socket.on('ai-response', (data) => {
      setAiThinking(false);
      setAiError('');
      setAiResponses((prev) => [...prev, {
        text: data.text,
        userText: data.userText,
        mode: data.mode,
        stealth: data.stealth,
        timestamp: data.timestamp
      }]);
    });

    socket.on('ai-error', (data) => {
      setAiThinking(false);
      setAiError(data.message);
    });

    socket.on('user-joined', (data) => {
      console.log('User joined:', data);
    });

    socket.on('webrtc-offer-request', ss.onOfferRequest);
    socket.on('webrtc-offer',         ss.onOffer);
    socket.on('webrtc-answer',        ss.onAnswer);
    socket.on('webrtc-ice',           ss.onIce);
    socket.on('peer-screen-share-start', ss.onPeerShareStart);
    socket.on('peer-screen-share-stop',  ss.onPeerShareStop);
    socket.on('remote-pointer',          ss.onRemotePointer);
    socket.on('remote-control-request',  ss.onControlRequest);
    socket.on('remote-control-response', ss.onControlResponse);
    socket.on('remote-click',  ss.onRemoteClick);
    socket.on('remote-scroll', ss.onRemoteScroll);
    socket.on('remote-key',    ss.onRemoteKey);

    socket.connect();
    socket.once('connect', () => {
      socket.emit('join-room', { roomKey, sessionKey });
    });

    return () => {
      socket.off('transcript');
      socket.off('message');
      socket.off('ai-thinking');
      socket.off('ai-response');
      socket.off('ai-error');
      socket.off('user-joined');
      socket.off('webrtc-offer-request'); socket.off('webrtc-offer');
      socket.off('webrtc-answer'); socket.off('webrtc-ice');
      socket.off('peer-screen-share-start'); socket.off('peer-screen-share-stop');
      socket.off('remote-pointer'); socket.off('remote-control-request');
      socket.off('remote-control-response');
      socket.off('remote-click'); socket.off('remote-scroll'); socket.off('remote-key');
      socket.disconnect();
      ss.cleanup();
    };
  }, [roomKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateRoom = async () => {
    const trimmedKey = sessionKey.trim();
    if (!trimmedKey) { setSessionError('Please enter your session key'); return; }
    setSessionError('');
    try {
      await api.post('/sessions/verify', { sessionKey: trimmedKey });
      const response = await api.post('/rooms/create');
      const key = response.data.room.roomKey;
      setSessionKey(trimmedKey);
      setRoomKey(key);
      setShowSessionInput(false);
    } catch (error) {
      setSessionError(error.response?.data?.error || 'Failed to create room. Please check your session key.');
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim() && roomKey) {
      socket.emit('send-message', { roomKey, message });
      setMessages((prev) => [...prev, {
        message,
        userId: user.id,
        userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'You',
        timestamp: new Date().toISOString(),
        self: true
      }]);
      setMessage('');
    }
  };

  const copyRoomKey = () => {
    navigator.clipboard.writeText(roomKey);
    showToast('Room key copied to clipboard!', 'success');
  };

  const launchStealthPopup = () => {
    const url = `/stealth?sk=${encodeURIComponent(sessionKey)}&rk=${encodeURIComponent(roomKey)}`;

    // Try to open on second monitor (right of primary screen)
    // window.screen.width = primary screen width; placing at that x puts it on monitor 2 if connected
    const w = 420, h = 640;
    const left = window.screen.width;   // right edge of primary = left edge of second monitor
    const top  = 40;

    const popup = window.open(
      url,
      'stealth-agent',
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,scrollbars=no,resizable=yes`
    );

    if (!popup) {
      showToast('Popup blocked! Allow popups for this site in your browser settings.', 'error');
      return;
    }

    showToast(
      'Stealth Agent launched. In Zoom: Share Screen → Window tab → select your main browser window (NOT full screen).',
      'success'
    );
  };

  // ── Session Input Screen ──────────────────────────────────────────────────
  if (showSessionInput) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <Toast toast={toast} />
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <button onClick={() => navigate('/dashboard')} className="mr-4 text-gray-600 hover:text-gray-900">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <h1 className="text-xl font-bold text-gray-800">Create Room</h1>
              </div>
            </div>
          </div>
        </nav>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-pink-600 rounded-full mb-4">
                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Create a Room</h2>
              <p className="text-gray-600">Enter your session key to begin</p>
            </div>
            {sessionError && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <p className="text-sm text-red-700">{sessionError}</p>
              </div>
            )}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Key</label>
              <input
                type="text"
                value={sessionKey}
                onChange={(e) => setSessionKey(e.target.value.trim())}
                placeholder="Enter your session key"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 font-mono text-sm"
              />
              <p className="mt-2 text-sm text-gray-500">Get your session key from your company administrator</p>
            </div>
            <button
              onClick={handleCreateRoom}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-pink-700 shadow-lg"
            >
              Generate Room Key
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Room Screen ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} />

      {/* AI Context Modal */}
      {showAiContextModal && (
        <AiContextModal
          onConfirm={handleAiContextConfirm}
          onCancel={() => setShowAiContextModal(false)}
        />
      )}

      {/* Nav */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-bold text-gray-800">Room: {roomKey}</h1>
              <button onClick={copyRoomKey} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-lg hover:bg-blue-200">
                Copy Key
              </button>
              {features.screenShare && (
                <button onClick={() => ss.isSharing ? ss.stopShare() : ss.startShare(roomKey)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                    ss.isSharing
                      ? 'bg-red-100 border-red-400 text-red-700'
                      : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                  }`}>
                  {ss.isSharing ? 'Stop Sharing' : 'Share Screen'}
                </button>
              )}
              <button
                onClick={launchStealthPopup}
                className="flex items-center space-x-1 px-3 py-1 bg-gray-900 text-green-400 text-xs rounded-lg hover:bg-gray-800 border border-gray-700 font-mono"
                title="Opens a private AI assistant — in Zoom, share a Window (not full screen) to keep it hidden"
              >
                <span className="h-1.5 w-1.5 bg-green-400 rounded-full animate-pulse inline-block"></span>
                <span>🕵️ Launch Stealth Agent</span>
              </button>
              {stealthMode && aiEnabled && (
                <span className="flex items-center space-x-1 px-2 py-1 bg-gray-900 text-green-400 text-xs rounded-full font-mono">
                  <span className="h-1.5 w-1.5 bg-green-400 rounded-full animate-pulse inline-block"></span>
                  <span>STEALTH</span>
                </span>
              )}
            </div>
            <div className="flex items-center">
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
                Leave Room
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {ss.remoteShares.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">
              Shared Screens ({ss.remoteShares.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ss.remoteShares.map(share => (
                <ScreenShareViewer
                  key={share.peerId}
                  share={share}
                  canControl={features.remoteControl}
                  controlGranted={ss.controlGranted === share.peerId}
                  onPointer={features.remoteControl ? ss.sendPointer : null}
                  onClick={ss.controlGranted === share.peerId ? ss.sendClick : null}
                  onRequestControl={features.remoteControl ? ss.requestControl : null}
                />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left: Transcript + Recorder */}
          <div className="lg:col-span-2 space-y-4">

            {/* AI Interview Assistant Panel */}
            <div className={`rounded-xl shadow-lg p-4 border-2 transition-colors ${
              aiEnabled
                ? stealthMode ? 'bg-gray-900 border-green-500' : 'bg-purple-50 border-purple-400'
                : 'bg-white border-gray-200'
            }`}>
              {/* Header row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${aiEnabled ? 'bg-purple-600' : 'bg-gray-300'}`}>
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                    </svg>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${aiEnabled && stealthMode ? 'text-white' : 'text-gray-800'}`}>
                      AI Interview Assistant
                    </p>
                    <p className={`text-xs ${aiEnabled && stealthMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {aiEnabled
                        ? <>
                            {aiRoleLabel && <span className="font-semibold text-purple-400">[{aiRoleLabel}] </span>}
                            {aiMode === 'coach'
                              ? 'Coach mode — private tips to help you respond'
                              : 'Answer mode — full answers you can read aloud'}
                          </>
                        : 'Disabled — enable to get AI help during your interview'}
                    </p>
                  </div>
                </div>

                {/* AI ON/OFF Toggle */}
                <button
                  onClick={handleAiToggle}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                    aiEnabled ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    aiEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Controls row — only when enabled */}
              {aiEnabled && (
                <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                  {/* Mode selector */}
                  <div className="flex rounded-lg overflow-hidden border border-gray-600">
                    <button
                      onClick={() => handleModeChange('coach')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        aiMode === 'coach'
                          ? 'bg-purple-600 text-white'
                          : stealthMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Coach
                    </button>
                    <button
                      onClick={() => handleModeChange('answer')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        aiMode === 'answer'
                          ? 'bg-purple-600 text-white'
                          : stealthMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Answer
                    </button>
                  </div>

                  {/* Stealth toggle */}
                  <button
                    onClick={handleStealthToggle}
                    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      stealthMode
                        ? 'bg-gray-800 border-green-500 text-green-400'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stealthMode
                        ? "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        : "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      } />
                    </svg>
                    <span>{stealthMode ? 'Stealth ON' : 'Stealth OFF'}</span>
                  </button>

                  {!stealthMode && (
                    <span className="text-xs text-orange-500 font-medium">AI responses visible to others</span>
                  )}

                  {/* Change role */}
                  <button
                    onClick={() => setShowAiContextModal(true)}
                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                      stealthMode
                        ? 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                        : 'border-gray-300 text-gray-500 hover:text-gray-700'
                    }`}
                    title="Change AI role / context"
                  >
                    ✎ Role
                  </button>

                  {/* Clear history */}
                  {aiResponses.length > 0 && (
                    <button
                      onClick={() => setAiResponses([])}
                      className={`text-xs px-2 py-1 rounded ${stealthMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* AI Response Area — only when enabled */}
              {aiEnabled && (
                <div className={`mt-3 rounded-lg p-3 h-36 overflow-y-auto text-sm space-y-2 ${
                  stealthMode ? 'bg-gray-800' : 'bg-purple-50'
                }`}>
                  {aiResponses.length === 0 && !aiThinking && (
                    <p className={`text-center text-xs mt-8 ${stealthMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {aiMode === 'coach'
                        ? 'Speak — I\'ll give you private coaching tips after each sentence.'
                        : 'Speak — I\'ll generate a full answer you can read aloud.'}
                    </p>
                  )}

                  {aiResponses.map((r, i) => (
                    <div key={i} className={`rounded-lg p-2.5 ${stealthMode ? 'bg-gray-700' : 'bg-white border border-purple-200'}`}>
                      <div className="flex items-center space-x-1 mb-1">
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          r.mode === 'coach' ? 'text-blue-400' : 'text-green-400'
                        }`}>
                          {r.mode === 'coach' ? 'Coach' : 'Answer'}
                        </span>
                        {r.stealth && <span className="text-xs text-gray-500">(private)</span>}
                      </div>
                      <p className={stealthMode ? 'text-gray-100' : 'text-gray-800'}>{r.text}</p>
                    </div>
                  ))}

                  {aiThinking && (
                    <div className={`rounded-lg p-2.5 flex items-center space-x-2 ${stealthMode ? 'bg-gray-700' : 'bg-white border border-purple-200'}`}>
                      <div className="flex space-x-1">
                        <div className="h-2 w-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="h-2 w-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="h-2 w-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className={`text-xs ${stealthMode ? 'text-gray-400' : 'text-gray-500'}`}>AI is thinking...</span>
                    </div>
                  )}

                  {aiError && (
                    <div className="rounded-lg p-2.5 bg-red-900 border border-red-600">
                      <p className="text-red-300 text-xs">{aiError}</p>
                    </div>
                  )}

                  <div ref={aiEndRef} />
                </div>
              )}
            </div>

            {/* Live Transcript */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Live Transcript</h2>
                {isRecording && (
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-red-600 font-medium">Recording</span>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm leading-relaxed">
                {finalTranscripts.length === 0 && !liveText && (
                  <p className="text-gray-400 text-sm text-center mt-8 font-sans">Start recording to see live transcription...</p>
                )}
                {finalTranscripts.map((t, i) => (
                  <div key={i} className="mb-3 border-b border-gray-200 pb-2 last:border-0">
                    <p className="text-gray-800 leading-relaxed">{t.text}</p>
                    <span className="text-xs text-gray-400">{new Date(t.timestamp).toLocaleTimeString()}</span>
                  </div>
                ))}
                {liveText && (
                  <div className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-blue-400 animate-pulse flex-shrink-0"></span>
                    <p className="text-blue-500 italic">{liveText}</p>
                  </div>
                )}
                <div ref={transcriptEndRef} />
              </div>

              <VoiceRecorder
                roomKey={roomKey}
                sessionKey={sessionKey}
                isRecording={isRecording}
                setIsRecording={setIsRecording}
              />
            </div>
          </div>

          {/* Right: Messages */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Messages</h2>
            <div className="bg-gray-50 rounded-lg p-4 h-80 overflow-y-auto mb-4">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-400 text-sm text-center">No messages yet.</p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={`mb-3 ${msg.self ? 'text-right' : 'text-left'}`}>
                    {!msg.self && (
                      <p className="text-xs text-gray-500 font-medium mb-0.5 ml-1">{msg.userName}</p>
                    )}
                    <div className={`inline-block px-4 py-2 rounded-lg max-w-xs ${msg.self ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-xs opacity-75 mt-1">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                    </div>
                    {msg.self && (
                      <p className="text-xs text-gray-400 mt-0.5 mr-1">You</p>
                    )}
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Send</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Screen Share Viewer sub-component ─────────────────────────────────────────
const ScreenShareViewer = ({ share, canControl, controlGranted, onPointer, onClick, onRequestControl }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && share.stream) videoRef.current.srcObject = share.stream;
  }, [share.stream]);

  const handleMouseMove = (e) => {
    if (!canControl || !onPointer) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onPointer(share.peerId, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
  };

  const handleClick = (e) => {
    if (!controlGranted || !onClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onClick(share.peerId, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
  };

  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden shadow-lg border border-gray-700">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-gray-200 text-xs font-medium">{share.peerName || 'Participant'} — Screen</span>
        </div>
        {canControl && !controlGranted && onRequestControl && (
          <button onClick={() => onRequestControl(share.peerId)}
            className="text-xs px-2 py-1 bg-yellow-700 hover:bg-yellow-600 text-yellow-100 rounded">
            Request Control
          </button>
        )}
        {controlGranted && <span className="text-xs text-yellow-400">Control active</span>}
      </div>
      <div className="relative">
        {share.stream ? (
          <video ref={videoRef} autoPlay playsInline muted
            className={`w-full ${canControl ? (controlGranted ? 'cursor-crosshair' : 'cursor-pointer') : ''}`}
            onMouseMove={handleMouseMove} onClick={handleClick} />
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-gray-800">
            <div className="text-center">
              <div className="h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-gray-400 text-xs">Connecting to stream...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateRoom;
